<script setup lang="ts">
    import Contact from '@/app/contact/models/Contact';
    import EndpointFactory from '~~/app/factories/EndpointFactory';
    import { useEvent, type ApplicationEvents } from '~/composables/useEventBus';
    import { ModelType } from '~~/app/base/schemas/BaseSchema';
    import InvoiceCollection from '~/app/invoicing/collections/InvoiceCollection';
    import ExpenseCollection from '~/app/invoicing/collections/ExpenseCollection';
    import Invoice from '~/app/invoicing/models/Invoice';
    import Expense from '~/app/invoicing/models/Expense';
    import InvoiceRoute from '~/classes/InvoiceRoute';
    import InvoicingStatisticsCollection from '~/classes/InvoicingStatisticsCollection';
    import ContactCollection from '~/app/contact/collections/ContactCollection';
    import { CONTACT_TYPE } from '~/app/contact/enums/ContactType';
    import { TENANT_SUBSCRIPTIONL_PLAN } from '~/app/auth/enums/TenantSubscriptionPlan';
    import { InvoiceDocumentType } from '~/app/invoicing/enums/InvoiceDocumentType';

    const { authed } = useAuthStore();

    const { modalName } = useModelModal<Contact>(
        Contact,
        (data: ModalPayload<Contact> & { collection?: ContactCollection }) => retrieve(data),
        () => {
            contact.value = null;
            invoices.value = null;
            quotes.value = null;
            expenses.value = null;
            profitability.value = null;
            collection.value = null;
        }
    );
    const loading: Ref<boolean> = ref(false);
    const loadingSecondary: Ref<boolean> = ref(false);
    const contact: Ref<Contact | null> = ref(null);
    const invoices: Ref<InvoiceCollection | null> = ref(null);
    const quotes: Ref<InvoiceCollection | null> = ref(null);
    const expenses: Ref<ExpenseCollection | null> = ref(null);
    const profitability: Ref<InvoicingStatisticsCollection | null> = ref(null);
    const collection: Ref<ContactCollection | null> = ref(null);

    const endpoint = EndpointFactory.make(ModelType.CONTACTS);
    const invoiceEndpoint = EndpointFactory.make(ModelType.INVOICES);

    // Check if the contact already has the required includes
    const hasRequiredIncludes = (contact: Contact): boolean => {
        return !!contact.defaultVatRate;
    };

    const retrieve = async (data: ModalPayload<Contact> & { collection?: ContactCollection }) => {
        // Store the collection if provided
        if (data.collection) {
            collection.value = data.collection;
        }

        // Reset secondary data
        invoices.value = null;
        quotes.value = null;
        expenses.value = null;
        profitability.value = null;

        // Use the already available contact to display immediately
        const initialContact = data.model;

        // Check if we need to retrieve the full contact
        const needsFullRetrieve = !hasRequiredIncludes(initialContact);
        let profitabilityLoaded = false;

        if (needsFullRetrieve) {
            // If includes are missing, load everything
            loading.value = true;
            const contactEndpoint = EndpointFactory.make(ModelType.CONTACTS);
            const response = await contactEndpoint.setInclude('defaultVatRate,invoicePaymentReminderGroup').retrieve(initialContact.getId(), { includeMeta: 'profitability' });
            contact.value = response.data;

            // Load profitability if available
            if (contact.value?.profitability) {
                profitability.value = new InvoicingStatisticsCollection(contact.value.profitability.statistics);
                profitabilityLoaded = true;
            }

            loading.value = false;
        } else {
            // Otherwise, use the existing contact immediately
            contact.value = initialContact;
        }

        // Load secondary data in the background (asynchronous)
        loadSecondaryData(profitabilityLoaded);
    };

    // Load secondary data (invoices, quotes, expenses, profitability) asynchronously
    const loadSecondaryData = async (profitabilityAlreadyLoaded: boolean = false) => {
        if (!contact.value) return;

        loadingSecondary.value = true;
        const contactId = contact.value.getId();
        const contactType = contact.value.contactType;

        try {
            // Load profitability if not already available
            if (!profitabilityAlreadyLoaded && !profitability.value) {
                const contactEndpoint = EndpointFactory.make(ModelType.CONTACTS);
                const response = await contactEndpoint.setInclude('defaultVatRate,invoicePaymentReminderGroup').retrieve(contactId, { includeMeta: 'profitability' });

                // Update the contact with includes if necessary
                if (response.data) {
                    contact.value.refresh(response.data);
                    if (response.data.profitability) {
                        profitability.value = new InvoicingStatisticsCollection(response.data.profitability.statistics);
                    }
                }
            }

            // If client: retrieve invoices and quotes in parallel
            if (contactType === CONTACT_TYPE.CLIENT || contactType === CONTACT_TYPE.SNAPSHOT) {
                await Promise.all([
                    // Retrieve the last 3 invoices
                    invoiceEndpoint
                        .index({
                            filter: {
                                contact: {
                                    id: [contactId],
                                },
                                documentType: 'invoice',
                                // status: '-sent', // TODO: TBD with product owner
                            },
                            page: {
                                number: 1,
                                size: 3,
                            },
                            sort: '-createdAt',
                        })
                        .then((response) => {
                            invoices.value = response.data;
                        }),
                    // Retrieve the last 3 quotes
                    invoiceEndpoint
                        .index({
                            filter: {
                                contact: {
                                    id: [contactId],
                                },
                                documentType: 'quote',
                            },
                            page: {
                                number: 1,
                                size: 3,
                            },
                            sort: '-createdAt',
                        })
                        .then((response) => {
                            quotes.value = response.data;
                        }),
                ]);
            }

            // If supplier: retrieve expenses
            if (contactType === CONTACT_TYPE.SUPPLIER) {
                const expenseEndpoint = EndpointFactory.make(ModelType.EXPENSES);
                const expensesResponse = await expenseEndpoint.setInclude('worksite').index({
                    filter: {
                        contact: {
                            id: [contactId],
                        },
                    },
                    page: {
                        number: 1,
                        size: 3,
                    },
                    sort: '-createdAt',
                });

                expenses.value = expensesResponse.data;
            }
        } catch (error) {
            useToasteoError();
        } finally {
            loadingSecondary.value = false;
        }
    };

    // Handle contact updates to refresh the modal
    const handleContactUpdated = async (updatedContact: Contact) => {
        if (contact.value && contact.value.getId() === updatedContact.getId()) {
            const oldContactType = contact.value.contactType;
            const newContactType = updatedContact.contactType;

            // Update the contact
            contact.value.refresh(updatedContact);

            // If contactType changed, reload all data by calling retrieve again
            if (oldContactType !== newContactType) {
                // Reset secondary data
                invoices.value = null;
                quotes.value = null;
                expenses.value = null;
                profitability.value = null;

                await retrieve({
                    model: updatedContact,
                    collection: collection.value ?? undefined,
                });
            }
        }
    };

    // Listen to contact updates
    useListen('contact:contact:updated', handleContactUpdated);

    const close = () => {
        useEvent(`${modalName}:close` as keyof ApplicationEvents);
    };

    // Trigger to manage contact
    const openContact = () => {
        if (contact.value) {
            contact.value.open();
            close();
        }
    };

    const editContact = () => {
        if (contact.value) {
            contact.value.edit();
            close();
        }
    };

    const deleteContact = () => {
        if (!contact.value) return;
        useEvent('contact:contact:context-menu:close');
        useEvent('contact:contact:destroy-alert:open', {
            callback: async () => {
                await endpoint.destroy(contact.value!.getId());
                close();
            },
            model: contact.value,
        });
    };

    const createQuote = () => {
        if (contact.value) {
            navigateTo(new InvoiceRoute(InvoiceDocumentType.QUOTE).createWithContact(contact.value.getId()));
            close();
        }
    };

    const createInvoice = () => {
        if (contact.value) {
            navigateTo(new InvoiceRoute(InvoiceDocumentType.INVOICE).createWithContact(contact.value.getId()));
            close();
        }
    };

    const createExpense = () => {
        if (contact.value) {
            navigateTo(`/invoicing/expenses/create?contact=${contact.value.getId()}`);
            close();
        }
    };

    const handleQuoteClick = (quote: Invoice) => {
        quote.edit();
        close();
    };

    const handleQuoteContextMenu = (quote: Invoice, event: MouseEvent) => {
        quote.openContextMenu(event);
    };

    const handleInvoiceClick = (invoice: Invoice) => {
        invoice.edit();
        close();
    };

    const handleInvoiceContextMenu = (invoice: Invoice, event: MouseEvent) => {
        invoice.openContextMenu(event);
    };

    const handleExpenseClick = (expense: Expense) => {
        expense.edit();
        close();
    };

    const handleExpenseContextMenu = (expense: Expense, event: MouseEvent) => {
        expense.openContextMenu(event);
    };

    // Navigation functions
    const currentIndex = computed(() => {
        if (!collection.value || !contact.value) return -1;
        return collection.value.items.findIndex((c) => c.getId() === contact.value!.getId());
    });

    const hasPrevious = computed(() => {
        return currentIndex.value > 0;
    });

    const hasNext = computed(() => {
        return collection.value && currentIndex.value >= 0 && currentIndex.value < collection.value.items.length - 1;
    });

    const navigateToPrevious = () => {
        if (!hasPrevious.value || !collection.value) return;
        const previousContact = collection.value.items[currentIndex.value - 1];
        useEvent('contact:contact:modal:open', {
            model: previousContact,
            collection: collection.value,
        });
    };

    const navigateToNext = () => {
        if (!hasNext.value || !collection.value) return;
        const nextContact = collection.value.items[currentIndex.value + 1];
        useEvent('contact:contact:modal:open', {
            model: nextContact,
            collection: collection.value,
        });
    };

    const profitabilityCards = useProfitabilityCards(profitability);
</script>

<template>
    <ModelModal
        :model="Contact"
        modal-style="right_panel"
    >
        <template #navigation>
            <UiAction
                :content="{
                    icon: 'fa-regular fa-xmark',
                    type: 'light',
                }"
                @click="close"
            />
            <UiAction
                :content="{
                    icon: 'fa-solid fa-chevron-up',
                    type: 'light',
                }"
                :is-disabled="!hasPrevious"
                @click="navigateToPrevious"
            />
            <UiAction
                :content="{
                    icon: 'fa-solid fa-chevron-down',
                    type: 'light',
                }"
                :is-disabled="!hasNext"
                @click="navigateToNext"
            />
        </template>
        <ModalLoader v-if="loading" />
        <TransitionFadeSlide>
            <div
                v-if="contact"
                class="!pb-12 !h-auto divide-y-0"
                :class="$theme('modal.right_panel.content') + $theme('modal.right_panel.section.container')"
            >
                <!-- Header with actions -->
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                        <div
                            v-if="contact.displayName"
                            class="flex shrink-0 items-center justify-center w-[60px] h-[60px] rounded-full bg-contact-initials text-white font-semibold text-xl"
                        >
                            {{ contact.getInitials() }}
                        </div>
                        <div class="inline-flex flex-col items-start">
                            <ContactContactTypeBadge :contact-type="contact.contactType" />
                            <h2 :class="$theme('modal.right_panel.title.bigText')">
                                {{ contact.displayName }}
                            </h2>
                        </div>
                    </div>
                    <div class="flex items-center gap-2">
                        <UiAction
                            :content="{
                                icon: 'fa-regular fa-eye',
                                type: 'light',
                            }"
                            modifier="big"
                            @click="openContact"
                        />
                        <UiAction
                            :content="{
                                icon: 'fa-regular fa-pencil',
                                type: 'light',
                            }"
                            modifier="big"
                            @click="editContact"
                        />
                        <!-- <UiAction
                            :content="{
                                icon: 'fa-regular fa-box-archive',
                                type: 'light',
                            }"
                            modifier="big"
                            @click="archiveContact"
                        /> -->
                        <UiAction
                            :content="{
                                icon: 'fa-regular fa-trash',
                                type: 'light',
                            }"
                            modifier="big"
                            @click="deleteContact"
                        />
                    </div>
                </div>

                <!-- Financial summary cards -->
                <div
                    v-if="contact?.contactType !== CONTACT_TYPE.SUPPLIER && (profitability || loadingSecondary)"
                    class="flex flex-wrap justify-between mt-5"
                >
                    <template v-if="profitability">
                        <InvoicingStatisticsCards
                            v-for="(card, index) in profitabilityCards"
                            :key="`${card.label}-${index}`"
                            :card="card"
                            class="w-full md:w-1/2 lg:w-[calc(25%-12px)] !max-w-none"
                        />
                    </template>
                    <template v-else-if="loadingSecondary">
                        <div
                            v-for="i in 4"
                            :key="`loading-${i}`"
                            class="w-full md:w-1/2 lg:w-[calc(25%-12px)] !max-w-none h-20 bg-gray-200 animate-pulse rounded"
                        />
                    </template>
                </div>

                <!-- Contact information -->
                <div class="flex justify-between gap-2 dashboard-panel__background border-2 border-[#ABB5C080] p-4 mt-8">
                    <div class="flex flex-col gap-2">
                        <div class="flex gap-2 text-[#42484E]">
                            <i class="text-[#8D929C] fa-solid fa-location-dot max" />
                            <span class="max-w-[150px] leading-tight">
                                {{ contact.address?.display ?? '-' }}
                            </span>
                        </div>
                        <div class="flex items-center gap-2 text-[#42484E]">
                            <i class="text-[#8D929C] fa-solid fa-envelope" />
                            <a
                                v-if="contact.email"
                                :href="`mailto:${contact.email}`"
                                class="underline hover:text-[#8D929C]"
                            >
                                {{ contact.email }}
                            </a>
                            <span v-else>-</span>
                        </div>
                        <div class="flex items-center gap-2 text-[#42484E]">
                            <i class="text-[#8D929C] fa-solid fa-phone" />
                            <a
                                v-if="contact.phone"
                                :href="`tel:${contact.phone}`"
                                class="underline hover:text-[#8D929C] flex items-center gap-2"
                            >
                                {{ contact.phone }}
                            </a>
                            <span v-else>-</span>
                        </div>

                        <div class="flex items-center gap-2 text-[#42484E]">
                            <i class="text-[#8D929C] fa-solid fa-percent" />
                            {{ contact.defaultVatRate?.title ?? '-' }}
                        </div>
                    </div>

                    <InvoicingContactsWritableNotes
                        class="contact-modal__notes max-w-[350px] flex-1 grow"
                        :contact="contact"
                    />
                </div>

                <!-- Quotes section (only for clients) -->
                <div
                    v-if="contact.contactType === CONTACT_TYPE.CLIENT || contact.contactType === CONTACT_TYPE.SNAPSHOT"
                    class="mt-6"
                >
                    <div class="flex items-center justify-between mb-4">
                        <h3 class="text-lg font-semibold flex items-center gap-2">
                            <i class="fa-thin fa-file-lines text-2xl" />
                            {{ $t('invoicing.quotes.singular_title') }}
                        </h3>
                        <UiAction
                            v-can="'invoicing:quote:create'"
                            :content="{
                                label: 'actions.add_quote',
                                icon: 'fa-regular fa-plus',
                                type: 'light',
                            }"
                            @click="createQuote"
                        />
                    </div>
                    <div
                        v-if="!authed?.hasPermission('invoicing:quote:view_any')"
                        class="text-center py-8 bg-[#ECF8F7] flex flex-col items-center gap-4"
                    >
                        <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full leading-tight bg-action-darkBlue text-action-cyan">
                            <i :class="`fa-solid fa-bolt text-lg`"></i>
                            <h3 class="text-sm font-bold uppercase font-inter">{{ $t(TENANT_SUBSCRIPTIONL_PLAN.PRO) }}</h3>
                        </div>
                        {{ $t('plan.no_access') }}

                        <UiAction
                            :content="{
                                label: 'app.misc.change_plan',
                                icon: 'fa-regular fa-arrow-up-right-from-square',
                                type: 'light',
                            }"
                            @click="navigateTo('/settings/subscription/plan'), close()"
                        />
                    </div>
                    <div
                        v-else-if="loadingSecondary"
                        class="text-center py-8 text-gray-500"
                    >
                        <i class="fa-solid fa-spinner fa-spin text-2xl mb-2" />
                    </div>
                    <div
                        v-else-if="!quotes || (quotes.isEmpty() && authed?.hasPermission('invoicing:quote:view_any'))"
                        class="text-center py-8 text-gray-500"
                    >
                        {{ $t('page.empty_content.quote') }}
                    </div>
                    <div v-else-if="authed?.hasPermission('invoicing:quote:view_any')">
                        <InvoicingQuoteListItem
                            v-for="(quote, index) in quotes.items.slice(0, 3)"
                            :key="quote.getInternalId()"
                            :content="quote"
                            :index="index"
                            @click="handleQuoteClick"
                            @contextmenu="handleQuoteContextMenu"
                        />
                    </div>
                </div>

                <!-- Invoices section (only for clients) -->
                <div
                    v-if="contact.contactType === CONTACT_TYPE.CLIENT || contact.contactType === CONTACT_TYPE.SNAPSHOT"
                    class="mt-6"
                >
                    <div
                        v-if="authed?.hasPermission('invoicing:invoice:view_any')"
                        class="flex items-center justify-between mb-4"
                    >
                        <h3 class="text-lg font-semibold flex items-center gap-2">
                            <i class="fa-thin fa-file-invoice-dollar text-2xl" />
                            {{ $t('invoicing.invoices.singular_title') }}
                        </h3>
                        <UiAction
                            v-can="'invoicing:invoice:create'"
                            :content="{
                                label: 'actions.add_invoice',
                                icon: 'fa-regular fa-plus',
                                type: 'light',
                            }"
                            @click="createInvoice"
                        />
                    </div>
                    <div
                        v-if="!authed?.hasPermission('invoicing:invoice:view_any')"
                        class="text-center py-8 bg-[#ECF8F7] flex flex-col items-center gap-4"
                    >
                        <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full leading-tight bg-action-darkBlue text-action-cyan">
                            <i :class="`fa-solid fa-bolt text-lg`"></i>
                            <h3 class="text-sm font-bold uppercase font-inter">{{ $t(TENANT_SUBSCRIPTIONL_PLAN.PRO) }}</h3>
                        </div>
                        {{ $t('plan.no_access') }}

                        <UiAction
                            :content="{
                                label: 'app.misc.change_plan',
                                icon: 'fa-regular fa-arrow-up-right-from-square',
                                type: 'light',
                            }"
                            @click="navigateTo('/settings/subscription/plan'), close()"
                        />
                    </div>
                    <div
                        v-else-if="loadingSecondary"
                        class="text-center py-8 text-gray-500"
                    >
                        <i class="fa-solid fa-spinner fa-spin text-2xl mb-2" />
                    </div>
                    <div
                        v-else-if="!invoices || (invoices.isEmpty() && authed?.hasPermission('invoicing:invoice:view_any'))"
                        class="text-center py-8 text-gray-500"
                    >
                        {{ $t('page.empty_content.invoice') }}
                    </div>
                    <div v-else-if="authed?.hasPermission('invoicing:invoice:view_any')">
                        <InvoicingInvoiceListItem
                            v-for="(invoice, index) in invoices.items.slice(0, 3)"
                            :key="invoice.getInternalId()"
                            :content="invoice"
                            :index="index"
                            @click="handleInvoiceClick"
                            @contextmenu="handleInvoiceContextMenu"
                        />
                    </div>
                </div>

                <!-- Expenses section (only for suppliers) -->
                <div
                    v-if="contact.contactType === CONTACT_TYPE.SUPPLIER && authed?.hasPermission('invoicing:expense:view_any')"
                    class="mt-6"
                >
                    <div
                        v-if="authed?.hasPermission('invoicing:expense:view_any')"
                        class="flex items-center justify-between mb-4"
                    >
                        <h3 class="text-lg font-semibold flex items-center gap-2">
                            <i class="fa-thin fa-money-bill text-2xl" />
                            {{ $t('invoicing.expenses.singular_title') }}
                        </h3>
                        <UiAction
                            v-can="'invoicing:expense:create'"
                            :content="{
                                label: 'actions.add_expense',
                                icon: 'fa-regular fa-plus',
                                type: 'light',
                            }"
                            @click="createExpense"
                        />
                    </div>
                    <div
                        v-if="!authed?.hasPermission('invoicing:expense:view_any')"
                        class="text-center py-8 bg-[#ECF8F7] flex flex-col items-center gap-4"
                    >
                        <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full leading-tight bg-action-darkBlue text-action-cyan">
                            <i :class="`fa-solid fa-bolt text-lg`"></i>
                            <h3 class="text-sm font-bold uppercase font-inter">{{ $t(TENANT_SUBSCRIPTIONL_PLAN.PRO) }}</h3>
                        </div>
                        {{ $t('plan.no_access') }}

                        <UiAction
                            :content="{
                                label: 'app.misc.change_plan',
                                icon: 'fa-regular fa-arrow-up-right-from-square',
                                type: 'light',
                            }"
                            @click="navigateTo('/settings/subscription/plan'), close()"
                        />
                    </div>
                    <div
                        v-else-if="loadingSecondary"
                        class="text-center py-8 text-gray-500"
                    >
                        <i class="fa-solid fa-spinner fa-spin text-2xl mb-2" />
                    </div>
                    <div
                        v-else-if="!expenses || (expenses.isEmpty() && authed?.hasPermission('invoicing:expense:view_any'))"
                        class="text-center py-8 text-gray-500"
                    >
                        {{ $t('page.empty_content.expense') }}
                    </div>
                    <div v-else-if="authed?.hasPermission('invoicing:expense:view_any')">
                        <InvoicingExpenseListItem
                            v-for="(expense, index) in expenses.items.slice(0, 3)"
                            :key="expense.getInternalId()"
                            :content="expense"
                            :index="index"
                            @click="handleExpenseClick"
                            @contextmenu="handleExpenseContextMenu"
                        />
                    </div>
                </div>

                <!-- Tasks section -->
                <div
                    v-if="contact.tasks?.isNotEmpty()"
                    :class="$theme('modal.right_panel.section.container')"
                >
                    <TaskTaskLinesTable
                        :key="contact.tasks?.getInternalId()"
                        :collection="contact.tasks"
                    />
                </div>
            </div>
        </TransitionFadeSlide>
    </ModelModal>
</template>

<style scoped>
    .contact-modal__notes {
        :deep(textarea) {
            @apply bg-[#F8F3E0] h-auto min-h-[150px];
        }
    }
</style>
